import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Mottagare = {
    id: number;
    namn: string;
    hsaid: string;
    is_active: boolean;
};

export type MottagareDisplay = {
    value: Mottagare["id"];
    label: Mottagare["namn"];
};

type State = BaseState & {
    mottagare: Map<Mottagare["id"], Mottagare>;
};

export const convertMottagareData: (args: { mottagare?: Mottagare }) => MottagareDisplay | null = ({ mottagare }) => {
    if (!mottagare) {
        return null;
    }

    return {
        value: mottagare.id,
        label: mottagare.namn,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        mottagare: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "mottagare";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        mottagare: (state: State) => [...state.mottagare.values()],
        mottagareSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertMottagareData({ mottagare: singleResult }));
            }

            return result;
        },
        mottagareById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.mottagare.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addMottagare: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                mottagare = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                mottagare: Mottagare[];
            }
        ) =>
            subsetSetter<State, Mottagare>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: mottagare,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchMottagare: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Mottagare>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "remiss/mottagare/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addMottagare", {
                    subsetName,
                    offset: resultOffset,
                    mottagare: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertMottagareData({ mottagare: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
